import React, { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, TextField, Button, Typography, Box, Alert, Snackbar } from "@mui/material"
import { Settings as SettingsIcon } from "@mui/icons-material"
import useSnackDispatcher from "../../_helpers/reusablefunctions/snackBarComponent"
import ApiService from "../../_services/ApiService"
import SnackbarContainer from "../Snackbar"

const initialFields = { number_of_days: "", collab_limit: "" }
const initialErrors = { number_of_days: "", collab_limit: "" }

export default function SettingsPage() {
  const { successSnack, errorSnack } = useSnackDispatcher()
  const [formData, setFormData] = useState(initialFields)
  const [errors, setErrors] = useState(initialErrors)

  // api call to get settings data
  useEffect(() => {
    ApiService.get("getSettings")
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          const data = response.data
          setFormData({
            id: data.id || "",
            number_of_days: data.number_of_days || "",
            collab_limit: data.collab_limit || "",
          })
        } else {
          errorSnack("Failed to load settings")
        }
      })
  }, [])

  const validateField = (name, value) => {
    if (!value) return "This field is required"
    if (isNaN(value) || !Number.isInteger(Number(value))) return "Please enter a valid integer"
    if (Number(value) <= 0) return "Please enter a positive number"
    if (Number(value) > 365) return "Maximum value is 365 days"
    return ""
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((f) => ({ ...f, [name]: value }))
    if (errors[name]) setErrors((e) => ({ ...e, [name]: "" }))
  }

  const handleBlur = (e) => {
    const { name, value } = e.target
    setErrors((e) => ({ ...e, [name]: validateField(name, value) }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const newErrors = Object.fromEntries(
      Object.entries(formData)
        .filter(([key]) => key !== "id")
        .map(([key, value]) => [key, validateField(key, value)])
    )
    setErrors(newErrors)
    if (Object.values(newErrors).every((err) => !err)) {
      const apiCall = formData.id
        ? ApiService.put("createUpdateSettings", formData, { id: formData.id })
        : ApiService.post("createUpdateSettings", formData)
      apiCall
        .then((response) => {
          if (response.status === 200 || response.status === 201) {
            setFormData(response.data.data[0].fields)
            successSnack("Settings updated successfully"); 
          } else {
            errorSnack("Failed to update settings")
          }
        })
        .catch((error) => {
          console.error("Error updating settings:", error)
          errorSnack("An error occurred while updating settings")
        })
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        padding: 3,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <Card
        sx={{
          maxWidth: 600,
          width: "100%",
          boxShadow: 3,
          borderRadius: 2,
        }}
      >
        <CardHeader
          avatar={<SettingsIcon color="primary" />}
          title={
            <Typography variant="h5" component="h1" fontWeight="bold">
              Settings
            </Typography>
          }
          subheader="Configure timing and limit rules for song pitching and collaborations"
          sx={{ pb: 1 }}
        />

        <CardContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              name="number_of_days"
              label="Days Before Song Can Be Pitched"
              type="number"
              value={formData.number_of_days}
              onChange={handleInputChange}
              onBlur={handleBlur}
              error={!!errors.number_of_days}
              helperText={
                errors.number_of_days || "Number of days a song must wait before it can be pitched for the first time"
              }
              inputProps={{ min: 1, max: 365 }}
              sx={{ mb: 3 }}
              variant="outlined"
              size="small"
            />

            {/* ---------------- collab count limit field --------------- */}

            <TextField
              fullWidth
              name="collab_limit"
              label="Collab Limit"
              type="number"
              value={formData.collab_limit}
              onChange={handleInputChange}
              onBlur={handleBlur}
              error={!!errors.collab_limit}
              helperText={
                errors.collab_limit || "Limit on the number of collaborations artist can have"
              }
              inputProps={{ min: 1, max: 365 }}
              sx={{ mb: 3 }}
              variant="outlined"
              size="small"
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="submit"
                variant="contained"
                className="gn-actionbtn"
              >
                Save
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <SnackbarContainer />
    </Box>
  )
}
